import React, { useEffect } from "react";
import {
  Modal,
  IconButton,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import { makeStyles } from "@mui/styles";
import { useSelector } from "react-redux";

const useStyles = makeStyles({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalContent: {
    position: "relative",
    width: "80%",
    maxWidth: "800px",
    height: "80vh",
    backgroundColor: "white",
    padding: "20px",
    boxShadow: 24,
    borderRadius: "8px",
    outline: "none",
    zIndex: 1300,
  },
  closeButton: {
    position: "absolute",
    top: 10,
    right: 10,
  },
  tableContainer: {
    width: "100%",
    height: "70vh",
    overflow: "auto",
    marginTop: "20px",
  },
  table: {
    minWidth: "800px",
  },
  tableCell: {
    textAlign: "center",
  },
  roleCell: {
    textAlign: "left",
  },
  legend: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "10px",
  },
  checkIcon: {
    color: "green",
  },
  readIcon: {
    color: "orange",
  },
  noAccessIcon: {
    color: "red",
  },
  submenu: {
    paddingLeft: "50px",
  },
});

const RoleChartModal = ({ open, onClose, fetchRoleChart, roleChart }) => {
  const organizationItems = useSelector(
    (state) => state.organizationReducer.items
  );
  const classes = useStyles();

  useEffect(() => {
    if (open) {
      fetchRoleChart();
    }
  }, [open, organizationItems]);
  const getAccessIcon = (access_type) => {
    if (access_type === "read_write") {
      return <CheckOutlinedIcon className={classes.checkIcon} />;
    } else if (access_type === "read") {
      return <CheckOutlinedIcon className={classes.readIcon} />;
    } else {
      return <CloseOutlinedIcon className={classes.noAccessIcon} />;
    }
  };

  const getRoleAccess = (roleId, menuOrSubmenu) => {
    const role = roleChart.find((role) => role.role.id === roleId);
    if (role) {
      const menuAccess = role.menus.find((m) => m.id === menuOrSubmenu.id);
      if (menuAccess) {
        return menuAccess.access_type;
      }
      for (let m of role.menus) {
        const submenuAccess = m.submenus.find(
          (sm) => sm.id === menuOrSubmenu.id
        );
        if (submenuAccess) {
          return submenuAccess.access_type;
        }
      }
    }
    return "none";
  };

  const renderMenuRows = (menus) => {
    return menus.map((menu) => (
      <React.Fragment key={menu.id}>
        <TableRow>
          <TableCell className={`${classes.tableCell} ${classes.roleCell}`}>
            {menu.name}
          </TableCell>
          {roleChart.map((roleData) => (
            <TableCell key={roleData.role.id} className={classes.tableCell}>
              {getAccessIcon(getRoleAccess(roleData.role.id, menu))}
            </TableCell>
          ))}
        </TableRow>
        {menu.dropdown &&
          menu.submenus.map((submenu) => (
            <TableRow key={submenu.id}>
              <TableCell className={`${classes.tableCell} ${classes.submenu}`}>
                <Typography variant="subtitle2">
                  <SubdirectoryArrowRightIcon sx={{ fontSize: "14px" }} />
                  {submenu.name}
                </Typography>
              </TableCell>
              {roleChart.map((roleData) => (
                <TableCell key={roleData.role.id} className={classes.tableCell}>
                  {getAccessIcon(getRoleAccess(roleData.role.id, submenu))}
                </TableCell>
              ))}
            </TableRow>
          ))}
      </React.Fragment>
    ));
  };

  return (
    <Modal
      open={open}
      onClose={(event) => event.stopPropagation()}
      aria-labelledby="role-chart-modal-title"
      aria-describedby="role-chart-modal-description"
      className={classes.modal}
    >
      <Box
        className={classes.modalContent}
        onClick={(e) => e.stopPropagation()}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>
        <Typography
          id="role-chart-modal-title"
          variant="h6"
          component="h2"
          gutterBottom
        >
          Role Chart
        </Typography>
        <Box className={classes.legend}>
          <Box display="flex" alignItems="center">
            <CheckOutlinedIcon className={classes.checkIcon} />
            <Typography variant="body2" sx={{ marginLeft: 1 }}>
              Full Access
            </Typography>
          </Box>
          <Box display="flex" alignItems="center">
            <CheckOutlinedIcon className={classes.readIcon} />
            <Typography variant="body2" sx={{ marginLeft: 1 }}>
              Read Access
            </Typography>
          </Box>
          <Box display="flex" alignItems="center">
            <CloseOutlinedIcon className={classes.noAccessIcon} />
            <Typography variant="body2" sx={{ marginLeft: 1 }}>
              No Access
            </Typography>
          </Box>
        </Box>
        <Box className={classes.tableContainer}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell
                  className={`${classes.tableCell} ${classes.roleCell}`}
                >
                  Role
                </TableCell>
                {roleChart.map((data, index) => (
                  <TableCell key={index} className={classes.tableCell}>
                    {data?.role?.role.charAt(0).toUpperCase() +
                      data?.role?.role.slice(1)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {roleChart[0]?.menus && renderMenuRows(roleChart[0]?.menus)}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Modal>
  );
};

export default RoleChartModal;
